import React from 'react';

import { ModalBody, ModalTransition } from '@atlaskit/modal-dialog';
import Spinner from '@atlaskit/spinner';

import { LoadableLazy } from '@confluence/loadable';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

/**
 * Lazily loaded modal that renders the third-party connector connect form.
 *
 * ModalTransition and Modal are rendered here (in the eagerly-loaded chunk)
 * so the modal backdrop appears immediately while the connector form chunk
 * is loading. The spinner renders inside the modal shell during load.
 *
 * Uses @confluence/loadable (not React.lazy) for Confluence-standard code
 * splitting with SSR compatibility and built-in error handling.
 *
 * The package uses react-relay so Confluence's existing Relay environment
 * is used directly — no additional provider wrapping is needed.
 */
const LazyConnectorModalContent = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-twcg-1489-connector-modal" */
				'./SetupConnectorModalContent.tsx'
			)
		).SetupConnectorModalContent,
	loading: () => (
		<ModalBody>
			<Spinner size="large" />
		</ModalBody>
	),
});

export interface SetupConnectorModalProps {
	connectorKey: string;
	onClose: () => void;
}

export function SetupConnectorModal({ connectorKey, onClose }: SetupConnectorModalProps) {
	const { Modal } = useChoreographerAssets();

	return (
		<ModalTransition>
			<Modal onClose={onClose} width="large">
				<LazyConnectorModalContent connectorKey={connectorKey} onClose={onClose} />
			</Modal>
		</ModalTransition>
	);
}
